import { inject, Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from '@env/environment';
import { Account } from 'app/core/models/account.types';
import { User } from 'app/core/models/user.types';
import { ReservedRole } from 'app/core/enums/permission';
import { WINDOW } from 'app/providers/window.provider';

@Injectable({
  providedIn: 'root',
})
export class PendoService {
  private static readonly CLEAR_SESSION_POLL_INTERVAL_MS = 250;

  private readonly window = inject<Window>(WINDOW);

  private _loaded = false;

  loadSdk(): void {
    if (this._loaded) {
      return;
    }

    if (!environment.pendo.apiKey) {
      console.warn('Pendo apiKey is missing from the environment config');
      return;
    }

    this._loaded = true;
    this.window.pendoLoad(environment.pendo.apiKey);
  }

  identify(user: User, account: Account): void {
    this.loadSdk();

    if (!this.window.pendo?.initialize) {
      console.warn('Pendo SDK is not available');
      return;
    }

    const missingFields = [!account.subdomain && 'account.subdomain', !user.email && 'user.email'].filter(Boolean);

    if (missingFields.length) {
      console.warn(`Pendo identify called without ${missingFields.join(' and ')}`);
      return;
    }

    const pendoAccount = {
      id: this.buildPendoAccountId(account.subdomain),
      name: account.name,
    };

    this.window.pendo.initialize({
      visitor: {
        id: this.buildVisitorId(user.email),
        isAdminUser: user.roles?.some((role) => role.name === ReservedRole.Admin) ?? false,
      },
      account: pendoAccount,
    });
  }

  clearSession(): void {
    if (!this._loaded) {
      return;
    }

    if (typeof this.window.pendo?.clearSession === 'function') {
      this.window.pendo.clearSession();
      return;
    }

    if (this.window.pendo?._failed) {
      console.warn('Pendo agent failed to load; skipping clearSession.');
      return;
    }

    setTimeout(() => this.clearSession(), PendoService.CLEAR_SESSION_POLL_INTERVAL_MS);
  }

  private buildVisitorId(email: string): string {
    return CryptoJS.MD5(CryptoJS.enc.Utf16LE.parse(email.toLowerCase())).toString().toUpperCase();
  }

  private buildPendoAccountId(subdomain: string): string {
    return environment.pendo?.envSuffix ? `${subdomain}-${environment.pendo.envSuffix}` : subdomain;
  }
}
